import React, { useEffect, useState } from "react";
import logo from "../../assets/images/logo.png";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Row,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { loginUser } from "../../store/actions";
import { createSelector } from "reselect";
import { useSnackbar } from "notistack";

const Login = () => {
  document.title = "Login | Valitan MIS";

  const [isDemo, setIsDemo] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const primaryColor = "#0147C8";
  const hoverColor = "#0137A0";

 
  // Detect environment (DEMO / LIVE)
  useEffect(() => {
    const currentHost = window.location.hostname;
    setIsDemo(!["valitan.co.tz", "www.valitan.co.tz"].includes(currentHost));
  }, []);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      username: "",
      password: "",
    },
    validationSchema: Yup.object({
      username: Yup.string().required("Please Enter Your Username"),
      password: Yup.string().required("Please Enter Your Password"),
    }),
    onSubmit: (values) => {
      if (rememberMe) {
        localStorage.setItem("rememberedUsername", values.username);
        localStorage.setItem("rememberedPassword", values.password);
      } else {
        localStorage.removeItem("rememberedUsername");
        localStorage.removeItem("rememberedPassword");
      }

      dispatch(loginUser(values, navigate));
    },
  });

  const { setValues } = validation;

  // Load remembered credentials
  useEffect(() => {
    const rememberedUsername = localStorage.getItem("rememberedUsername");
    const rememberedPassword = localStorage.getItem("rememberedPassword");
    if (rememberedUsername && rememberedPassword) {
      setRememberMe(true);
      setValues({
        username: rememberedUsername,
        password: rememberedPassword,
      });
    }
  }, [setValues]);

  const loginSelector = createSelector(
    (state) => state.login,
    (loginState) => ({
      error: loginState.error,
      authUser: loginState.authUser,
    })
  );

  const { error, authUser } = useSelector(loginSelector);

  // Show error toast
  useEffect(() => {
    if (error) {
      enqueueSnackbar(error, { variant: "error" });
      console.error("Login error:", error);
    }
  }, [enqueueSnackbar, error]);

  // Redirect on successful login
  useEffect(() => {
    if (authUser) {
      navigate("/dashboard");
    }
  }, [authUser, navigate]);

  // Set page background
  useEffect(() => {
    document.body.style.backgroundColor = "#f5f6fa";
    document.body.className = "";
    return () => {
      document.body.style.backgroundColor = "";
    };
  }, []);

  return (
    <React.Fragment>
      <div
        className="account-pages d-flex align-items-center justify-content-center"
        style={{
          minHeight: "100vh",
          backgroundColor: "#f5f6fa",
        }}
      >
        <Container>
          <Row className="justify-content-center">
            <Col lg={6} md={8} xl={4}>
              <Card
                className="shadow-sm border-0"
                style={{
                  borderRadius: "12px",
                  backgroundColor: "#ffffff",
                }}
              >
                <CardBody className="p-4">
                  {/* LOGO */}
                  <div className="text-center mb-3">
                    <Link to="/">
                      <img
                        src={logo}
                        alt="Valitan Logo"
                        height="60"
                        className="auth-logo logo-dark mx-auto mb-2"
                      />
                    </Link>
                  </div>

                  {/* HEADER */}
                  <p className="mb-3 text-center text-dark">
                    <strong>Welcome back!</strong>
                    <br />
                    Sign in to continue to Valitan IMS.
                  </p>

                  <p
                    className={`mb-3 text-center fw-bold ${isDemo ? "text-danger" : ""}`}
                    style={{ color: isDemo ? "#DC3545" : primaryColor }}
                  >
                    {isDemo ? "(DEMO)" : "(LIVE)"}
                  </p>

                  {/* FORM */}
                  <Form
                    className="form-horizontal"
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                      return false;
                    }}
                  >
                    <Row>
                      <Col md={12}>
                        {/* USERNAME */}
                        <div className="mb-4">
                          <Label className="form-label text-dark">Username</Label>
                          <Input
                            name="username"
                            className="form-control"
                            placeholder="Enter username"
                            type="text"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.username}
                            invalid={
                              validation.touched.username && validation.errors.username
                            }
                          />
                          {validation.touched.username && validation.errors.username ? (
                            <FormFeedback type="invalid">
                              {validation.errors.username}
                            </FormFeedback>
                          ) : null}
                        </div>

                        {/* PASSWORD */}
                        <div className="mb-4">
                          <Label className="form-label text-dark">Password</Label>
                          <InputGroup>
                            <Input
                              name="password"
                              type={showPassword ? "text" : "password"}
                              placeholder="Enter Password"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.password}
                              invalid={
                                validation.touched.password && validation.errors.password
                              }
                            />
                            <InputGroupText
                              onClick={() => setShowPassword(!showPassword)}
                              style={{
                                cursor: "pointer",
                                background: "transparent",
                                borderLeft: "none",
                              }}
                            >
                              <i
                                className={`mdi ${showPassword ? "mdi-eye-off" : "mdi-eye"
                                  } text-muted`}
                              ></i>
                            </InputGroupText>
                          </InputGroup>
                          {validation.touched.password && validation.errors.password ? (
                            <FormFeedback type="invalid">
                              {validation.errors.password}
                            </FormFeedback>
                          ) : null}
                        </div>

                        {/* REMEMBER ME + FORGOT PASSWORD */}
                        <Row className="align-items-center">
                          <Col>
                            <div
                              className="form-check d-flex align-items-center"
                              style={{ gap: "8px" }}
                            >
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id="customControlInline"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                                style={{
                                  accentColor: primaryColor,
                                  borderColor: primaryColor,
                                  cursor: "pointer",
                                  width: "18px",
                                  height: "18px",
                                }}
                              />
                              <label
                                className="form-label form-check-label text-dark"
                                htmlFor="customControlInline"
                                style={{ cursor: "pointer" }}
                              >
                                Remember me
                              </label>
                            </div>
                          </Col>
                          <Col className="col-7">
                            <div className="text-md-end mt-3 mt-md-0">
                              <Link
                                to="/auth-recoverpw"
                                style={{
                                  color: primaryColor,
                                  fontWeight: "500",
                                  textDecoration: "none",
                                  transition: "color 0.2s ease-in-out",
                                }}
                                onMouseEnter={(e) => (e.target.style.color = hoverColor)}
                                onMouseLeave={(e) => (e.target.style.color = primaryColor)}
                              >
                                <i className="mdi mdi-lock"></i> Forgot your password?
                              </Link>
                            </div>
                          </Col>
                        </Row>

                        {/* SUBMIT */}
                        <div className="d-grid mt-4">
                          <button
                            className="btn waves-effect waves-light"
                            type="submit"
                            style={{
                              backgroundColor: primaryColor,
                              color: "#fff",
                              fontWeight: "500",
                              letterSpacing: "0.5px",
                              border: "none",
                              padding: "10px 0",
                              transition: "background-color 0.2s ease-in-out",
                            }}
                            onMouseEnter={(e) => (e.target.style.backgroundColor = hoverColor)}
                            onMouseLeave={(e) => (e.target.style.backgroundColor = primaryColor)}
                          >
                            Log In
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </Form>
                </CardBody>
              </Card>

              {/* FOOTER */}
              <div className="mt-4 text-center">
                <p className="text-muted small mb-0">
                  © {new Date().getFullYear()} Valitan | Information Management System
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Login;
